/**
 * Desktop rail for both web apps: a floating card pinned to the icon width.
 *
 * It never expands. Titles come from the menu buttons' tooltips on hover, and
 * what an open rail used to show in full — the name, Conta / Equipe, sign-out,
 * the HWYAH attribution — sits in the account menu at the foot. So there is no
 * toggle, no drag rail, and Ctrl+B does nothing.
 */
import type { ComponentProps, ReactNode } from 'react';
import SidebarWordmark from './SidebarWordmark';
import UserAccountMenu from './UserAccountMenu';
import HwyahLogo from '../../components/HwyahLogo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
} from '../../components/ui/sidebar';
import { cn } from '../../lib/utils';

const stayCollapsed = () => {};
const HWYAH_SITE = 'https://hwyah.com';

/** `SidebarProvider` locked to the collapsed state. */
export function FloatingSidebarProvider(props: ComponentProps<typeof SidebarProvider>) {
  return <SidebarProvider {...props} open={false} onOpenChange={stayCollapsed} />;
}

interface Props {
  homeHref: string;
  /** Null when signed out: the foot shows the HWYAH credit instead. */
  account?: {
    displayName: string | null;
    photoURL: string | null;
    onSignOut?: () => void;
    billing?: boolean;
  } | null;
  /** Under the nav, above the account item — e.g. a sign-in prompt. */
  footer?: ReactNode;
  /** White on work pages; `inverted` (the dark palette) on account and settings. */
  tone?: 'light' | 'inverted';
  children: ReactNode;
}

export default function FloatingSidebar({
  homeHref,
  account,
  footer,
  tone = 'light',
  children,
}: Props) {
  return (
    <Sidebar
      variant="floating"
      collapsible="icon"
      data-tone={tone}
      className={cn(
        // Hugs its content, centred on the left edge, instead of spanning the window.
        '!bottom-auto !top-1/2 !h-auto -translate-y-1/2',
        '[&>[data-sidebar=sidebar]]:max-h-[calc(100svh-1rem)] [&>[data-sidebar=sidebar]]:!rounded-full',
        tone === 'inverted' ? 'dark' : '[--sidebar:#ffffff]',
        // Shadcn sets the text colour on the outer wrapper, outside the tone's palette.
        'text-sidebar-foreground',
      )}
    >
      <SidebarHeader className="items-center pb-5 pt-4">
        <SidebarWordmark href={homeHref} />
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent className="group-data-[collapsible=icon]:overflow-y-auto [scrollbar-width:none]">
        {children}
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter className="items-center pt-5">
        {footer}
        {account ? (
          <UserAccountMenu {...account} />
        ) : (
          <SidebarMenu className="w-auto">
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="x9 is a Product of HWYAH">
                <a href={HWYAH_SITE} target="_blank" rel="noopener noreferrer">
                  <HwyahLogo monochrome className="!size-auto h-4 text-muted-foreground" />
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
